import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import arrowsLeft from 'assets/images/icons/double-arrow-left.svg';
import arrowsRight from 'assets/images/icons/double-arrow-right.svg';
import TextField from 'components/form-components/TextField';
import { cloneDeep } from 'lodash';

const SelectionComponent = ({ title, list, selectedList, setSelectedList, error }) => {
  const { t } = useTranslation();
  const [originalList, setOriginalList] = useState([...cloneDeep(list)]);

  const moveSelection = (direction, selectedItem) => {
    if (direction === 'forward') {
      setSelectedList([...selectedList, selectedItem]);
    } else if (direction === 'backward') {
      const tempList = [...selectedList];
      setSelectedList([...tempList.filter((item) => item.id !== selectedItem.id)]);
    }
  };

  const moveAll = (direction) => {
    if (direction === 'forward') setSelectedList([...cloneDeep(originalList)]);
    else if (direction === 'backward') setSelectedList([]);
  };

  useEffect(() => {
    setOriginalList([...cloneDeep(list)]);
  }, [list]);

  return (
    <div className='selection-component-container'>
      <div className='d-flex gap-2 align-items-center'>
        <div className='vertical-line'></div>
        <label className='theme-text-green-1 theme-size-1_1'>{t(title)}</label>
      </div>
      <div className='selection-component'>
        <div className='d-flex gap-2 theme-background-white-2'>
          <SelectionBox
            direction='forward'
            originalList={originalList}
            selectedIds={selectedList.map((item) => item.id)}
            moveSelection={moveSelection}
            moveAll={moveAll}
          />
          <SelectionBox
            direction='backward'
            originalList={originalList}
            selectedIds={selectedList.map((item) => item.id)}
            moveSelection={moveSelection}
            moveAll={moveAll}
          />
        </div>

        <div className='theme-background-green-1 pt-1'></div>
      </div>
      {error && <p className='form-field-error my-3'>{t('error ' + error)}</p>}
    </div>
  );
};

SelectionComponent.propTypes = {
  title: PropTypes.string.isRequired,
  list: PropTypes.array.isRequired,
  selectedList: PropTypes.array.isRequired,
  setSelectedList: PropTypes.func.isRequired,
  error: PropTypes.string,
};

const SelectionBox = ({ direction, originalList, selectedIds, moveSelection, moveAll }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const getList = (direction) => {
    const list = [...cloneDeep(originalList)];
    return list.filter(
      (item) =>
        (direction === 'forward'
          ? !selectedIds.includes(item.id)
          : selectedIds.includes(item.id)) &&
        item.name.toLowerCase().includes(search.toLowerCase()),
    );
  };
  const filteredListLength = useMemo(() => getList(direction).length, [selectedIds, search]);

  return (
    <div className='w-100 p-3'>
      <label className='theme-font-inter-medium theme-text-black-1 mb-2'>
        {t(direction === 'forward' ? 'text_not_selected' : 'text_selected')}
      </label>
      <TextField
        name='search'
        value={search}
        placeholder={t('field_search')}
        handleChange={(_, value) => setSearch(value)}
        classes='w-100'
        shrink
      />
      <div className='selection-box'>
        <div>
          <img
            src={direction === 'forward' ? arrowsRight : arrowsLeft}
            className='back-arrow'
            onClick={() => moveAll(direction)}
          />
        </div>
        <div className='list'>
          {getList(direction).map((item, index) => (
            <div key={index} onClick={() => moveSelection(direction, item)}>
              <label>{item.name}</label>
            </div>
          ))}
        </div>
      </div>
      <label className='theme-size-0_9 theme-font-inter-medium'>
        {filteredListLength === 0
          ? t('text_no_records')
          : filteredListLength === originalList.length
          ? `${t('text_show_all')} ${originalList.length}`
          : `${t('text_showing')} ${filteredListLength} ${t('text_out_of')} ${originalList.length}`}
      </label>
    </div>
  );
};

SelectionBox.propTypes = {
  direction: PropTypes.string.isRequired,
  originalList: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
  moveSelection: PropTypes.func.isRequired,
  moveAll: PropTypes.func.isRequired,
};

export default SelectionComponent;
